/**
 * Email integration endpoints used by chat result-cards (reply/compose/send).
 *
 * These mirror the four raw `fetch` calls that previously lived inline in
 * `ChatBox.onCardAction`. They are intentionally thin and preserve the EXACT
 * request shapes (URL, method, headers, body) of the original inline calls so
 * the extraction is behavior-preserving:
 *
 *   - headers are `{ 'Content-Type': 'application/json',
 *       Authorization: 'Bearer ' + (token ?? ''),
 *       'X-Workspace-Id': getSelectedWorkspaceId() ?? '' }`
 *     i.e. the auth token is the caller-supplied React-closure token (NOT the
 *     storage token from `email-api.getHeaders`), and the workspace header is
 *     always present (empty string when unknown).
 *   - `/thread`, `/draft-reply` and `/send` hit `emailApiBase()`
 *     (`/workspaces/{ws}/integrations/email`).
 *   - `crmDraftEmail` hits `/workspaces/{ws}/crm-engine/draft-email` on the
 *     API base directly.
 *
 * Callers own all toast/error UX — these helpers only perform the request and
 * return the raw `Response` (or, for `draftReply`, the SSE text) so the card
 * layer keeps its existing control flow.
 */
import { env } from '@/lib/env';
import { getActiveWorkspaceId, getSelectedWorkspaceId } from '@/lib/api/workspace-context';
import { emailApiBase } from '@/lib/email-api';

export function cardActionEmailHeaders(token: string | null | undefined): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token ?? ''}`,
    'X-Workspace-Id': getSelectedWorkspaceId() ?? '',
  };
}

export interface FetchThreadArgs {
  threadId: string;
  accountId?: string;
}

/** POST /thread — pull a thread's messages for reply context. */
export function fetchEmailThread(token: string | null | undefined, args: FetchThreadArgs): Promise<Response> {
  return fetch(`${emailApiBase()}/thread`, {
    method: 'POST',
    headers: cardActionEmailHeaders(token),
    body: JSON.stringify({ threadId: args.threadId, ...(args.accountId ? { accountId: args.accountId } : {}) }),
  });
}

export interface DraftReplyArgs {
  threadId: string;
  subject?: string;
  messages: Array<{ from: unknown; body: string; date: unknown }>;
  tone?: 'professional' | 'casual';
  accountId?: string;
}

/** POST /draft-reply — request an AI reply draft (SSE text response). */
export function draftEmailReply(token: string | null | undefined, args: DraftReplyArgs): Promise<Response> {
  return fetch(`${emailApiBase()}/draft-reply`, {
    method: 'POST',
    headers: cardActionEmailHeaders(token),
    body: JSON.stringify({
      threadId: args.threadId,
      subject: args.subject,
      messages: args.messages,
      draftMode: 'xavior_ai',
      tone: args.tone,
      ...(args.accountId ? { accountId: args.accountId } : {}),
    }),
  });
}

export interface CrmDraftEmailArgs {
  contactId: string;
  actionType: string;
  composeStyle: 'casual' | 'professional';
  rewriteFrom?: string;
  avoidBody?: string;
}

/** POST /crm-engine/draft-email — compose a polished email from rough text. */
export function crmDraftEmail(token: string | null | undefined, args: CrmDraftEmailArgs): Promise<Response> {
  return fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/crm-engine/draft-email`, {
    method: 'POST',
    headers: cardActionEmailHeaders(token),
    body: JSON.stringify({
      contactId: args.contactId,
      actionType: args.actionType,
      composeStyle: args.composeStyle,
      ...(args.rewriteFrom && args.rewriteFrom.trim() ? { rewriteFrom: args.rewriteFrom } : {}),
      ...(args.avoidBody && args.avoidBody.trim() ? { avoidBody: args.avoidBody } : {}),
    }),
  });
}

export interface SendEmailArgs {
  to: string;
  subject: string;
  body: string;
  /** Carbon-copy recipients (comma-separated). */
  cc?: string;
  /** Blind-carbon-copy recipients (comma-separated). */
  bcc?: string;
  threadId?: string;
  messageId?: string;
  accountId?: string;
  /** Task B3: objectId of a Xavior doc/note to attach as a PDF. Forwarded to
   *  /send as `docAttachments: [{ objectId }]`, which renders the doc server-side
   *  to `<title>.pdf` (workspace-scoped, size-guarded). */
  attachDocId?: string;
}

/** POST /send — send (or reply to) an email. */
export function sendEmail(token: string | null | undefined, args: SendEmailArgs): Promise<Response> {
  return fetch(`${emailApiBase()}/send`, {
    method: 'POST',
    headers: cardActionEmailHeaders(token),
    body: JSON.stringify({
      to: args.to,
      subject: args.subject,
      body: args.body,
      ...(args.cc ? { cc: args.cc } : {}),
      ...(args.bcc ? { bcc: args.bcc } : {}),
      ...(args.threadId ? { threadId: args.threadId } : {}),
      ...(args.messageId ? { messageId: args.messageId } : {}),
      ...(args.accountId ? { accountId: args.accountId } : {}),
      // Task B3: attach a Xavior doc as a PDF — the /send route renders + attaches
      // it server-side. Only present when the card carried an attachment.
      ...(args.attachDocId ? { docAttachments: [{ objectId: args.attachDocId }] } : {}),
    }),
  });
}
